import { useMemo, useState, useCallback } from "react";
import {
  Loader2, CheckCircle2, AlertTriangle, ChefHat,
  Image as ImageIcon, Sparkles, Package, X, List,
} from "lucide-react";
import { useImageGeneration } from "../context/ImageGenerationContext";
import BatchDetailModal from "./BatchDetailModal";
import { useRestaurant } from "../context/RestaurantContext";

/**
 * Floating progress dock shown bottom-right whenever an AI batch is active.
 * Consumes `activeBatches` from ImageGenerationContext.
 *
 * One card per active kind (images / recipes / allergens / ingredients) with:
 *   - Live X/Y counter + percentage bar.
 *   - ETA inferred from elapsed time and throughput (avg seconds per task).
 *   - Failed counter (auto-refund already handled by backend, shown for info).
 *   - Close button: user can dismiss any card.
 *   - Detail button: opens per-dish status modal with retry capability.
 *   - Auto-dismiss 6s after completion (handled in the context).
 */
const KIND_META = {
  recipes:     { Icon: ChefHat,     color: "from-emerald-500 to-teal-500", tint: "bg-emerald-500" },
  images:      { Icon: ImageIcon,   color: "from-sky-500 to-indigo-500",   tint: "bg-sky-500" },
  allergens:   { Icon: Sparkles,    color: "from-violet-500 to-fuchsia-500", tint: "bg-violet-500" },
  ingredients: { Icon: Package,     color: "from-amber-500 to-orange-500", tint: "bg-amber-500" },
};

function fmtSec(total) {
  if (total <= 0 || !Number.isFinite(total)) return "—";
  if (total < 60) return `${Math.round(total)}s`;
  const m = Math.floor(total / 60);
  const s = Math.round(total % 60);
  return `${m}m ${s}s`;
}

function ProgressCard({ kind, info, onDismiss, onOpenDetail }) {
  const { Icon, color, tint } = KIND_META[kind] || KIND_META.recipes;
  const total = info.total || 0;
  const done = info.done || 0;
  const failed = info.failed || 0;
  const finished = done + failed;
  const pct = total > 0 ? Math.min(100, Math.round((finished / total) * 100)) : 0;
  const isDone = info.status === "completed" || (total > 0 && finished >= total);

  const eta = useMemo(() => {
    if (isDone || !info.started_at || finished === 0) return null;
    const elapsed = (Date.now() - info.started_at) / 1000;
    const avg = elapsed / finished;
    const remaining = (total - finished) * avg;
    return remaining;
  }, [info.started_at, finished, total, isDone]);

  return (
    <div
      className="bg-background/95 backdrop-blur border rounded-xl shadow-lg p-3 min-w-[260px] max-w-[320px]"
      data-testid={`batch-progress-${kind}`}
    >
      {/* Header row */}
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center text-white flex-shrink-0`}>
          {isDone ? (
            failed > 0
              ? <AlertTriangle className="w-3.5 h-3.5" />
              : <CheckCircle2 className="w-3.5 h-3.5" />
          ) : (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate flex items-center gap-1.5">
            <Icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            {info.label || kind}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {isDone
              ? (failed > 0
                  ? `Terminado · ${done} ok · ${failed} con error`
                  : `Terminado · ${done} generada${done === 1 ? "" : "s"}`)
              : `${finished} de ${total}`}
          </div>
        </div>
        <div className="text-xs font-medium tabular-nums text-muted-foreground" data-testid={`batch-progress-${kind}-pct`}>
          {pct}%
        </div>
        {/* Close button */}
        <button
          onClick={onDismiss}
          className="ml-1 p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          aria-label="Cerrar"
          data-testid={`batch-progress-${kind}-close`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-muted rounded-full overflow-hidden relative">
        <div
          className={`h-full ${tint} transition-[width] duration-500 ease-out`}
          style={{ width: `${pct}%` }}
        />
        {failed > 0 && total > 0 && (
          <div
            className="absolute top-0 right-0 h-full bg-red-400/70"
            style={{ width: `${Math.min(100, (failed / total) * 100)}%` }}
          />
        )}
      </div>

      {/* Footer: ETA / state + detail button */}
      <div className="mt-1.5 text-[10.5px] text-muted-foreground flex items-center justify-between gap-2">
        {!isDone ? (
          <>
            <span className="truncate">{failed > 0 ? `${failed} con error · ` : ""}Puedes seguir trabajando</span>
            {eta !== null && <span className="tabular-nums flex-shrink-0">~{fmtSec(eta)} restantes</span>}
          </>
        ) : (
          <span className="italic">Se ocultará en unos segundos…</span>
        )}

        {/* Detail button — always shown if we have a batch_id */}
        {info.batch_id && (
          <button
            onClick={onOpenDetail}
            className="flex-shrink-0 flex items-center gap-0.5 text-primary hover:underline text-[10.5px] font-medium"
            data-testid={`batch-progress-${kind}-detail`}
          >
            <List className="w-3 h-3" />
            Ver platos
          </button>
        )}
      </div>
    </div>
  );
}

export default function BatchProgressDock() {
  const { activeBatches, dismissBatch } = useImageGeneration();
  const { currentRestaurant } = useRestaurant();
  const [detailModal, setDetailModal] = useState(null); // { kind, batchId }

  const openDetail = useCallback((kind, batchId) => {
    setDetailModal({ kind, batchId });
  }, []);

  const kinds = Object.keys(activeBatches || {});
  if (kinds.length === 0 && !detailModal) return null;

  return (
    <>
      {kinds.length > 0 && (
        <div
          className="fixed bottom-24 right-4 lg:bottom-4 z-[60] flex flex-col gap-2 pointer-events-auto"
          data-testid="batch-progress-dock"
        >
          {kinds.map((k) => (
            <ProgressCard
              key={k}
              kind={k}
              info={activeBatches[k]}
              onDismiss={() => dismissBatch(k)}
              onOpenDetail={() => openDetail(k, activeBatches[k]?.batch_id)}
            />
          ))}
        </div>
      )}

      {detailModal && (
        <BatchDetailModal
          open={!!detailModal}
          onClose={() => setDetailModal(null)}
          kind={detailModal.kind}
          batchId={detailModal.batchId}
          restaurantId={currentRestaurant?.restaurant_id}
        />
      )}
    </>
  );
}
